import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  Check,
  AutoAwesome,
  ShoppingCart,
  EmojiEvents,
  PhotoCamera,
  CalendarToday,
  Security,
  Speed,
  Favorite,
  QrCodeScanner,
  Analytics,
  AttachMoney,
  Star,
  ArrowForward,
  CheckCircle,
  MenuBook,
  Article
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import blogPosts from '../data/blogPosts';

function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    try { if (window.bsaTrack) window.bsaTrack('cta_click', { metadata: { button: 'get_started' } }); } catch (e) {}
    navigate('/auth');
  };

  const handleAIGetStarted = () => {
    try { if (window.bsaTrack) window.bsaTrack('cta_click', { metadata: { button: 'ai_get_started' } }); } catch (e) {}
    navigate('/get-started');
  };

  const truncate = (str, n) => (str.length > n ? str.slice(0, n - 1) + '…' : str);

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          {/* Promo Banner */}
          <Box sx={{ bgcolor: '#FFD700', color: '#1B5E20', p: 2, mb: 3, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 1, fontSize: { xs: '16px', sm: '1.125rem' } }}>
              🎉 Special Launch Offer: Try All 3 Modules Free → Get 3 Months Free!
            </Typography>
            <Button
              variant="contained"
              onClick={handleAIGetStarted}
              aria-label="Claim special launch offer"
              sx={{
                bgcolor: '#1B5E20',
                color: 'white',
                fontWeight: 'bold',
                minHeight: 48,
                minWidth: 48,
                px: 3,
                // Ensure adequate touch target for mobile (Google Play requirement)
                touchAction: 'manipulation',
                '&:hover': { bgcolor: '#0D47A1' }
              }}
            >
              Claim Offer
            </Button>
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: { xs: 'stretch', md: 'flex-end' }, alignItems: 'center', gap: 2, mb: 2, position: 'relative', zIndex: 10 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/blog')}
              startIcon={<Article />}
              aria-label="Read our blog"
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.6)',
                fontWeight: 'bold',
                fontSize: { xs: '0.875rem', md: '1rem' },
                borderRadius: 3,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                // Ensure 44x44px touch target for mobile accessibility
                minHeight: 48,
                minWidth: 48,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'white' }
              }}
            >
              Blog
            </Button>
            <Button
              variant="outlined"
              onClick={handleGetStarted}
              size="large"
              aria-label="Login or sign up"
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.875rem', md: '1rem' },
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                borderWidth: '2px',
                // Ensure 44x44px touch target for mobile accessibility
                minHeight: 48,
                minWidth: { xs: 'auto', md: '140px' },
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderWidth: '2px'
                }
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'inline' } }}>Login / Sign Up</Box>
              <Box sx={{ display: { xs: 'inline', md: 'none' } }}>Login</Box>
            </Button>
          </Box>
          
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Chip
                  label="All Ages Welcome"
                  sx={{ fontWeight: 'bold', fontSize: '1rem', px: 2, bgcolor: '#B71C1C', color: 'white' }}
                />
                <Chip
                  icon={<Star />}
                  label="🎉 14-Day Free Trial"
                  sx={{ fontWeight: 'bold', bgcolor: '#BF360C', color: 'white' }}
                />
              </Box>
              <Typography
                variant="h1"
                component="h1"
                fontWeight="bold"
                gutterBottom
                sx={{ 
                  fontSize: { xs: 'clamp(1.6rem, 6.5vw, 2.5rem)', md: '3.5rem' },
                  color: '#FFB366',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                Turn Chores into Cheers! 🤖
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, fontSize: { xs: '1rem', sm: '1.5rem' }, lineHeight: 1.5 }}>
                The AI-powered family app that makes chores fun, teaches kids responsibility, and brings families together — with age-appropriate features for every child.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Primary CTA — AI onboarding */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAIGetStarted}
                  sx={{
                    bgcolor: 'white',
                    color: '#1B5E20',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 1, md: 1.5 },
                    minHeight: 48,
                    minWidth: 48,
                    width: { xs: '100%', md: 'auto' },
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                  endIcon={<ArrowForward sx={{ display: { xs: 'none', md: 'inline' } }} />}
                >
                  <Box sx={{ display: { xs: 'none', md: 'inline' } }}>Chat with AI to Get Started</Box>
                  <Box sx={{ display: { xs: 'inline', md: 'none' } }}>Chat with AI</Box>
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 1, md: 1.5 },
                    minHeight: '48px',
                    minWidth: '48px',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: { xs: 'none', md: 'inline' } }}>Skip to Signup</Box>
                  <Box sx={{ display: { xs: 'inline', md: 'none' } }}>Skip</Box>
                </Button>
              </Box>
              <Box sx={{ mt: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1.5, md: 3 }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Free forever plan</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>No credit card needed</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Cancel anytime</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  p: 4,
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  maxWidth: '100%',
                  width: '100%'
                }}
              >
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: 'center' }}>
                  🤖 Meet Your AI Assistants!
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, opacity: 0.9 }}>
                  Interactive chat with Earn-E & Burt
                </Typography>
                
                {/* Earn-E Chat Window */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.95)', 
                    color: 'text.primary', 
                    borderRadius: 2, 
                    p: 3,
                    border: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: 'rgba(135, 206, 235, 0.8)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #87CEEB',
                        boxShadow: '0 0 10px rgba(135, 206, 235, 0.5)'
                      }}>
                        <Typography sx={{ fontSize: '20px' }}>🤖</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                          Chip & Earn-E (Kids' Bots)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Chip (under 13) • Earn-E (ages 13-17)
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      bgcolor: 'grey.50', 
                      borderRadius: 2, 
                      p: 2, 
                      borderLeft: '4px solid #4CAF50',
                      fontStyle: 'italic'
                    }}>
                      <Typography variant="body2">
                        "Hi! I'm Chip for younger kids and Earn-E for teens — your kids' friendly helpers! Chip is pre-programmed and 100% COPPA-safe. Earn-E adds AI smarts for ages 13-17. Both are safe, encouraging, and always in your corner!"
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Burt Chat Window */}
                <Box>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.95)', 
                    color: 'text.primary', 
                    borderRadius: 2, 
                    p: 3,
                    border: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: 'rgba(70, 130, 180, 0.8)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #4682B4',
                        boxShadow: '0 0 10px rgba(70, 130, 180, 0.5)'
                      }}>
                        <Typography sx={{ fontSize: '20px' }}>🤖</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                          Burt (Parent's AI Assistant)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Generative AI - Parent-Only (18+)
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      bgcolor: 'grey.50', 
                      borderRadius: 2, 
                      p: 2, 
                      borderLeft: '4px solid #2196F3',
                      fontStyle: 'italic'
                    }}>
                      <Typography variant="body2">
                        "Hello! I'm Burt, your personal AI assistant. Using powerful generative AI, 
                        I can suggest chores, categorize shopping items, and help you plan. This feature 
                        is available for parents (18+) only."
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problem Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
          Sound Familiar? 😫
        </Typography>
        <Typography variant="h3" component="h3" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Common struggles every family faces
        </Typography>
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">
          {[
            { icon: '😤', title: 'Chore Wars', desc: 'Constant nagging to get chores done' },
            { icon: '💸', title: 'Allowance Chaos', desc: 'Lost track of who earned what' },
            { icon: '🛒', title: 'Shopping Confusion', desc: 'Forgotten items and impulse buys' },
            { icon: '📅', title: 'Schedule Madness', desc: 'Family calendar is a mess' },
            { icon: '📸', title: 'No Accountability', desc: "Can't verify if chores are done" },
            { icon: '⏰', title: 'Time Wasted', desc: 'Manually tracking everything' }
          ].map((problem, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
              <Paper elevation={2} sx={{ p: 3, width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h1" component="div" aria-hidden="true" sx={{ fontSize: '3rem', mb: 2 }}>
                  {problem.icon}
                </Typography>
                <Typography variant="h4" component="h4" fontWeight="600" gutterBottom>
                  {problem.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {problem.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Solution Section */}
      <Box sx={{ bgcolor: '#1B5E20', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom sx={{ color: 'white' }}>
            Introducing Earn-E Chore Helper! ✨
          </Typography>
          <Typography variant="h3" component="h3" textAlign="center" sx={{ mb: 6, opacity: 0.95, color: 'white' }}>
            The smart solution that transforms family life
          </Typography>
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {[
              {
                icon: <EmojiEvents sx={{ fontSize: 60 }} />,
                title: 'Gamified Chores',
                desc: 'Kids actually WANT to do chores to earn rewards and badges!'
              },
              {
                icon: <AttachMoney sx={{ fontSize: 60 }} />,
                title: 'Smart Allowance',
                desc: 'Automatic tracking, instant updates, and savings goals'
              },
              {
                icon: <PhotoCamera sx={{ fontSize: 60 }} />,
                title: 'Photo Verification',
                desc: 'Kids submit before/after photos for parent approval'
              },
              {
                icon: <ShoppingCart sx={{ fontSize: 60 }} />,
                title: 'AI Shopping Lists',
                desc: 'AI-powered shopping suggestions, smart categorization, and price estimates to keep your household budget on track.'
              },
              {
                icon: <CalendarToday sx={{ fontSize: 60 }} />,
                title: 'Family Calendar',
                desc: 'Built-in calendar to track events and never miss important dates'
              },
              {
                icon: <AutoAwesome sx={{ fontSize: 60 }} />,
                title: 'Parent\'s AI Assistant',
                desc: "Burt, our generative AI assistant, helps parents plan, suggests chores, and categorizes shopping items. (Parent-Only Feature)"
              }
            ].map((solution, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {solution.icon}
                  </Box>
                  <Typography variant="h3" component="h3" fontWeight="600" gutterBottom sx={{ color: 'white' }}>
                    {solution.title}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    {solution.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
          Powerful Features for Modern Families 💪
        </Typography>
        <Typography variant="h3" component="h3" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Everything you need in one beautiful app
        </Typography>
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {[
            {
              icon: <MenuBook color="primary" sx={{ fontSize: 50 }} />,
              title: 'Recipe Manager & Scanner',
              desc: 'Scan recipe photos with OCR, adjust servings, save family favorites, and share recipes',
              premium: false
            },
            {
              icon: <QrCodeScanner color="primary" sx={{ fontSize: 50 }} />,
              title: 'Barcode Scanner',
              desc: 'Scan any product barcode for instant details and pricing',
              premium: true
            },
            {
              icon: <PhotoCamera color="primary" sx={{ fontSize: 50 }} />,
              title: 'Photo Uploads',
              desc: 'Upload product photos or screenshots to add to your list',
              premium: true
            },
            {
              icon: <Analytics color="primary" sx={{ fontSize: 50 }} />,
              title: 'Analytics Dashboard',
              desc: 'Track spending, task completion, and family productivity',
              premium: true
            },
            {
              icon: <Security color="primary" sx={{ fontSize: 50 }} />,
              title: 'Child Safety Monitoring',
              desc: 'Real-time conversation monitoring with instant push alerts to parents if a child shows signs of distress. Always free — safety is never a premium feature.'
            },
            {
              icon: <Speed color="primary" sx={{ fontSize: 50 }} />,
              title: 'Lightning Fast',
              desc: 'Real-time updates across all devices instantly'
            },
            {
              icon: <Favorite color="primary" sx={{ fontSize: 50 }} />,
              title: 'Kid-Friendly',
              desc: 'Beautiful, engaging interface designed for all ages'
            },
            {
              icon: <Security color="warning" sx={{ fontSize: 50 }} />,
              title: 'Screen Time Rules (Android only)',
              desc: 'Lock your child\'s device to the Earn-E app during chores and scheduled events. Emergency 911 and parent call buttons always accessible. iOS support coming soon.'
            },
            {
              icon: <Check color="primary" sx={{ fontSize: 50 }} />,
              title: 'Family Messages',
              desc: 'Private family-only messaging. Kids can always reach parents — even during screen lock. COPPA-compliant with full parent oversight.'
            }
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
              <Card sx={{ height: '100%', position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
                {feature.premium && (
                  <Chip
                    label="PREMIUM"
                    color="secondary"
                    size="small"
                    sx={{ position: 'absolute', top: 16, right: 16 }}
                  />
                )}
                <CardContent sx={{ textAlign: 'center', py: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" component="h4" fontWeight="600" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Simple, Transparent Pricing 💰
          </Typography>
          <Typography variant="h3" component="h3" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Start free, upgrade when you're ready
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {/* Free Tier */}
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%', border: '2px solid', borderColor: 'grey.300' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Basic (Free)
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h2" fontWeight="bold" sx={{ display: 'inline' }}>
                      $0
                    </Typography>
                    <Typography component="span" sx={{ display: 'inline', ml: 1 }} color="text.secondary">
                      /forever
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={handleGetStarted}
                    sx={{ mb: 3, fontSize: '1rem', fontWeight: 600, py: 1.5, color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#1B5E20', bgcolor: 'rgba(27,94,32,0.04)' } }}
                  >
                    Get Started Free
                  </Button>
                  <Divider sx={{ mb: 3 }} />
                  <List dense>
                    {[
                      'Unlimited family members',
                      'Unlimited tasks & chores',
                      'Allowance tracking & savings goals',
                      'Shopping lists with AI suggestions',
                      'Family calendar',
                      'Photo verification for chores',
                      'Chip (safe bot for kids under 13)',
                      'Earn-E AI chat (teens 13-17)',
                      '🛡️ Safety monitoring & parent alerts',
                      'Profile customization'
                    ].map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Check color="success" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Premium Tier */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  height: '100%',
                  border: '3px solid',
                  borderColor: 'primary.main',
                  position: 'relative',
                  boxShadow: 6
                }}
              >
                <Chip
                  icon={<Star />}
                  label="MOST POPULAR"
                  color="warning"
                  sx={{
                    position: 'absolute',
                    top: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}
                />
                <CardContent sx={{ p: 4, bgcolor: '#1B5E20', color: 'white' }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
                    Premium
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h2" fontWeight="bold" sx={{ display: 'inline', color: 'white' }}>
                      $9.99
                    </Typography>
                    <Typography component="span" sx={{ display: 'inline', ml: 1, color: 'white' }}>
                      /month
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      or <strong>$99/year</strong> (save $20!)
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      mb: 3,
                      bgcolor: 'white',
                      color: '#1B5E20',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      py: 1.5,
                      minHeight: 48,
                      minWidth: 48,
                      '&:hover': {
                        bgcolor: 'grey.100'
                      }
                    }}
                    endIcon={<ArrowForward />}
                  >
                    Start 14-Day Free Trial
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 3, opacity: 0.8 }}>
                    No credit card required • Cancel anytime
                  </Typography>
                  <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.3)' }} />
                </CardContent>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: '#1B5E20' }}>
                    EVERYTHING IN BASIC, PLUS:
                  </Typography>
                  <List dense>
                    {[
                      '🤖 Burt — full generative AI for parents',
                      '📱 Barcode scanner',
                      '📸 Photo upload scanner',
                      '♾️ Unlimited photo & media storage',
                      '📊 Advanced analytics dashboard',
                      '🎨 Premium themes & customization',
                      '🔁 Recurring & scheduled tasks',
                      '💰 Unlimited AI price estimates',
                      '⚡ Early access to new features'
                    ].map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Check color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Earn-E */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
          Why Families Love Earn-E ❤️
        </Typography>
        <Typography variant="h3" component="h3" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Built by parents, for parents
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {[
            {
              icon: '🎯',
              title: 'Teaches Responsibility',
              desc: 'Kids learn the value of work and money through real-world experiences'
            },
            {
              icon: '🤝',
              title: 'Reduces Family Conflict',
              desc: 'Clear expectations and fair rewards = less arguing and more harmony'
            },
            {
              icon: '💡',
              title: 'AI-Powered Smart',
              desc: "Three AI helpers built for your family: Chip (safe scripted bot for under-13), Earn-E (AI chat for teens 13-17), and Burt (powerful generative AI for parents only)."
            },
            {
              icon: '🔒',
              title: 'Safe & Private',
              desc: 'Your family data is encrypted, secure, and never sold to advertisers'
            },
            {
              icon: '📱',
              title: 'Works Everywhere',
              desc: 'Web app works on any device - phone, tablet, or computer'
            },
            {
              icon: '💪',
              title: 'Always Improving',
              desc: 'We listen to your feedback and add new features regularly'
            }
          ].map((reason, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h1" component="div" aria-hidden="true" sx={{ fontSize: '4rem', mb: 2 }}>
                  {reason.icon}
                </Typography>
                <Typography variant="h4" component="h4" fontWeight="600" gutterBottom>
                  {reason.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, lineHeight: 1.5, minHeight: '72px' }}>
                  {reason.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Frequently Asked Questions 🤔
          </Typography>
          <Box sx={{ mt: 6 }}>
            {[
              {
                q: 'Is there really a free plan?',
                a: 'Yes! Our Basic plan is 100% free forever. No credit card, no tricks. You get unlimited tasks, allowance tracking, and all the core features families need.'
              },
              {
                q: 'What happens after the 14-day trial?',
                a: "You'll automatically switch to our free Basic plan. No charges, no surprises. You keep all your data and can upgrade to Premium anytime."
              },
              {
                q: 'Can I cancel anytime?',
                a: "Absolutely! Cancel your Premium subscription anytime and you'll keep access until the end of your billing period. Then you'll switch to the free plan."
              },
              {
                q: 'Is my family data safe?',
                a: "Yes! We use bank-level encryption, never sell your data, and comply with all privacy regulations. Your family's information is secure with us."
              },
              {
                q: 'How many family members can I add?',
                a: "Unlimited! Add as many parents, kids, and extended family members as you want on both free and premium plans."
              },
              {
                q: 'Do kids need email addresses?',
                a: "Nope! Only the parent creating the family needs an email. Kids can join with a simple family invite code."
              },
              {
                q: 'Is the app safe for young children (under 13)?',
                a: "Absolutely. For children under 13, we use Chip — a fully pre-programmed helper with no generative AI, no data collection, and no internet-based AI calls. Chip is 100% COPPA-compliant and designed to be safe, encouraging, and age-appropriate. Earn-E, our AI chat for teens 13-17, has separate age-appropriate guardrails. Parents always stay in control."
              },
              {
                q: 'How does AI work, and is it safe for my kids?',
                a: "We take child safety very seriously. For children under 13, Chip is a fully pre-programmed helper — no generative AI, no data stored, 100% COPPA-compliant. For teens 13-17, Earn-E is an AI assistant with age-appropriate guardrails and real-time safety monitoring. Burt, our full generative AI, is parent-only and restricted to users 18+. If a conversation ever raises a concern, parents receive an instant push notification — no login required."
              }
            ].map((faq, index) => (
              <Paper key={index} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h4" component="h4" fontWeight="600" gutterBottom sx={{ color: '#1B5E20' }}>
                  {faq.q}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {faq.a}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* From Our Blog Section */}
      <Box sx={{ background: 'linear-gradient(135deg, #f5f7ff 0%, #e6eefc 100%)', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            💡 Tips & Advice for Parents
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Practical, research-backed advice to help your family thrive. Read our latest posts.
          </Typography>
          <Grid container spacing={3}>
            {blogPosts.slice(0,3).map((post) => (
              <Grid item xs={12} md={4} key={post.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" component="h4" fontWeight="600" gutterBottom>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {truncate(post.excerpt, 120)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {post.readTime}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button 
                      size="medium" 
                      onClick={() => navigate(`/blog/${post.slug}`)} 
                      endIcon={<ArrowForward />}
                      aria-label={`Read more about ${post.title}`}
                      sx={{ 
                        fontSize: '0.875rem', 
                        fontWeight: 600, 
                        color: '#1B5E20',
                        minHeight: 48,
                        minWidth: 48
                      }}
                    >
                      Read More →
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button 
              variant="text" 
              onClick={() => navigate('/blog')} 
              endIcon={<ArrowForward />}
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 600,
                minHeight: 48,
                minWidth: 48
              }}
            >
              View All Posts →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 10,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight="bold" gutterBottom>
            Ready to Transform Your Family Life? 🚀
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            Start building better family habits today — chores, allowance, and teamwork, all in one place!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            sx={{
              bgcolor: 'white',
              color: '#1B5E20',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              px: 6,
              py: 2,
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
            endIcon={<ArrowForward />}
          >
            Start Your Free Trial
          </Button>
          <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
            No credit card required • 14-day full access • Cancel anytime
          </Typography>
          <Typography variant="caption" sx={{ mt: 2, opacity: 0.7, display: 'block' }}>
            For families with children of all ages. Parent account required (18+).
          </Typography>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
}

export default LandingPage;
